/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Account info general types
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.11.0
 */

import type { ITableButtonTypes } from '@components/Table/types/tableFooterButtonsTypes';
import type { INoticePageIdTypes } from '@modules/notification/types/noticePageIdTypes';

export enum EAddressElement {
    COUNTRY = 'COUNTRY',
    REGION = 'REGION',
    DISTRICT = 'DISTRICT',
    CITY = 'CITY',
    VILLAGE = 'VILLAGE',
    PLANNING_ELEMENT = 'PLANNING_ELEMENT',
    STREET = 'STREET',
    HOUSE = 'HOUSE',
    BLOCK = 'BLOCK',
    ZIP = 'ZIP',
    STRUCTURE = 'STRUCTURE',
    APARTMENT = 'APARTMENT'
};

export enum EConnectionStatusId {
    CONNECTION_IN_PROGRESS = 0, // подключается
    CONNECTED = 1, // подключено
    DISCONNECT_IN_PROGRESS = 2, // подключено, отключается
};

export enum EEdmAllowedActions {
    EDM_CONNECTION_ADD = 'EDM_CONNECTION_ADD',
    EDM_CONNECTION_VIEW = 'EDM_CONNECTION_VIEW',
};

interface IAddressElement {
    def: string;
    id?: string;
    type?: EAddressElement;
};

export interface IAccountDeliveryAddressInfo {
    addressId: string,
    addressString:  string,
    airId: string;
    apartment: IAddressElement | null;
    block: IAddressElement | null;
    city: IAddressElement | null;
    country: IAddressElement | null;
    district: IAddressElement | null;
    house: IAddressElement | null;
    planningElement: IAddressElement | null;
    region: IAddressElement | null;
    street: IAddressElement | null;
    structure: IAddressElement | null;
    zip: IAddressElement | null;
};

interface IDeliveryData {
    address: IAccountDeliveryAddressInfo;
    airId: string;
    contactPerson: string | null;
    email: string | null;
    note: string | null;
    phone: string | null;
    useCourier: boolean;
    useEdm: boolean;
    useEmail: boolean;
    usePost: boolean,
};

export interface IConnection {
    allowCancel: boolean;   // признак отображения кнопки "Отмена заявки"
    allowResponsibleEdit: boolean,
    allowPersonNameEdit: boolean,
    allowEmailEdit: boolean,
    allowPhoneNumberEdit: boolean,
    allowOperatorChange: boolean,
    creationDate?: string;
    isRoaming?: boolean,
    id: number;
    operatorName?: string;
    responsibleEmail?: string;
    responsibleName?: string;
    responsiblePhone?: string;
    statusDate?: string;
    statusId?: EConnectionStatusId;
    statusName?: string;
};

export interface IEdmDeliveryInfo {
    allowedActions: EEdmAllowedActions[]
    connections: IConnection[];
    descriptionLink: string;
    isConnectVisible: boolean;  // отображать кнопку "Подключить оператора ЭДО"
    isEnabled: boolean;
    isVisible: boolean;
    macroregionName?: string;
    showDeadlineNotification: boolean;
    inquiryCode?: string;
};

export interface IPostDeliveryInfo {
    isEnabled: boolean;
};

export interface IDeliveryInfoData {
    deliveryCountryId: number;
    deliveryData: IDeliveryData;
    edm?: IEdmDeliveryInfo;
    isAgreementEditingWarningShown: boolean;
    post?: IPostDeliveryInfo;
};

export interface IAccountDeliveryPageInfo {
    deliveryInfoData: IDeliveryInfoData;
    isDeliveryTabShown: boolean;
};

interface IAddInfo {
    agreementActiveAccountsCount?: number;
    agreementId?: number;
    contract: string;   // Договор
    id?: number;
    inn: string;        // ИНН
    kpp: string;        // КПП
    number?: string;
    serviceType?: string;
    subsCount: string;  // Абонентов
};

interface ICompanyInfo {
    sectorName?: string;  // Отрасль
    staffCount?: string;  // Количество сотрудников
    staffCountAvailable: boolean;
};

interface IJurInfo {
    address?: string;        // Адрес
    contactName?: string;    // Контактное лицо
    email?: string;          // Электронный адрес
    leaderName?: string;     // Руководитель
    phone?: string;          // Контактный телефон
};

interface IPersonalManager {
    info: string;
    name?: string;
    phone?: string;
};

export interface IAccountInfoGeneralMetaResponse extends INoticePageIdTypes {
    accountDeliveryPageInfo: IAccountDeliveryPageInfo;
    accountNumber: string;
    // Дополнительная информация
    addInfo?: IAddInfo;
    // Кнопки для футера
    buttons: ITableButtonTypes[];
    // Данные о компании из SFA
    companyInfo?: ICompanyInfo;
    id: number;
    isBisMode: boolean;
    // Под вопросом
    isSingle: boolean;
    // Юридическая информация из OAPI
    jurInfo?: IJurInfo;
    // Общая информация
    name: string;
    // Персональный менеджер
    personalManager?: IPersonalManager;
    phoneMask: string;
    subdivisionName?: string;
    subscribersInRoamingCount?: number;
}

export type TAccountInfoButtonsEnabled = { [buttonName: string]: boolean };
